import React from 'react'
import { LandUsePlanDocument, DocumentSource } from '@popety-io/types'
import { Modal } from '../../../../../molecules/Modal'
import { Icon } from '../../../../../atoms/Icon'
import { DocumentBrowser } from '../DocumentBrowser'
import { PDFViewerPanel } from '../PDFViewerPanel'
import {
  ModalContainer,
  SplitContainer,
  DocumentBrowserPanel,
  PDFViewerPanel as PDFPanel,
  ModalHeader,
  ModalHeaderTopRow,
  ModalHeaderContext,
  ModalHeaderChevron,
  ModalHeaderDivider,
  ModalTitle,
  CloseButton,
} from './DocumentViewerModal.styles'

export interface DocumentViewerModalTranslations {
  modalTitle?: string
  searchPlaceholder?: string
  searchAriaLabel?: string
  clearSearchAriaLabel?: string
  noDocumentSelected?: string
  selectDocumentHint?: string
  important?: string
  noPreviewAvailable?: string
  noPreviewHint?: string
  noDocuments?: string
  sourceTitles?: Partial<Record<DocumentSource, string>>
  documentTypes?: {
    rule?: string
    map?: string
  }
}

const DEFAULT_TRANSLATIONS: DocumentViewerModalTranslations = {
  modalTitle: 'Zone Documents',
  searchPlaceholder: 'Search documents...',
  searchAriaLabel: 'Search documents',
  clearSearchAriaLabel: 'Clear search',
  noDocumentSelected: 'No Document Selected',
  selectDocumentHint: 'Select a document from the list to view its contents',
  important: 'Important',
  noPreviewAvailable: 'No Preview Available',
  noPreviewHint: 'This document does not have a PDF preview URL available',
  noDocuments: 'No documents available',
  sourceTitles: {
    lupa: 'Local Plan Documents',
    lupc: 'Cantonal Plan Documents',
    lupo: 'Overlay Plan Documents',
    lup: 'Communal Plan Documents',
    region: 'Cantonal Documents',
    country: 'Federal Documents (Switzerland)',
  },
  documentTypes: {
    rule: 'rule',
    map: 'map',
  },
}

interface DocumentViewerModalProps {
  isOpen: boolean
  onClose: () => void
  selectedDocument: LandUsePlanDocument | null
  initialSource: DocumentSource
  documentsBySource: Partial<Record<DocumentSource, LandUsePlanDocument[]>>
  precedenceOrder: DocumentSource[]
  onSelectDocument: (document: LandUsePlanDocument) => void
  zoningService: any
  translations?: DocumentViewerModalTranslations
  locale?: string
  /** Zone context for breadcrumb header */
  municipalityName?: string
  planName?: string
  zoneName?: string
  /** Optional: transform a PDF URL through a proxy to bypass CORS */
  getPdfProxyUrl?: (url: string) => string
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  isOpen,
  onClose,
  selectedDocument,
  initialSource,
  documentsBySource,
  precedenceOrder,
  onSelectDocument,
  translations,
  locale,
  municipalityName,
  planName,
  zoneName,
  getPdfProxyUrl,
}) => {
  // Merge translations with defaults
  const t = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
    sourceTitles: {
      ...DEFAULT_TRANSLATIONS.sourceTitles,
      ...translations?.sourceTitles,
    },
    documentTypes: {
      ...DEFAULT_TRANSLATIONS.documentTypes,
      ...translations?.documentTypes,
    },
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      fullScreen={true}
      showCloseButton={false}
    >
      <ModalContainer>
        <ModalHeader>
          {(municipalityName || planName || zoneName) && (
            <>
              <ModalHeaderContext>
                {municipalityName && <span>{municipalityName}</span>}
                {municipalityName && (planName || zoneName) && (
                  <ModalHeaderChevron />
                )}
                {planName && <span>{planName}</span>}
                {planName && zoneName && <ModalHeaderChevron />}
                {zoneName && <span>{zoneName}</span>}
              </ModalHeaderContext>
              <ModalHeaderDivider />
            </>
          )}
          <ModalHeaderTopRow>
            <ModalTitle>{t.modalTitle}</ModalTitle>
            <CloseButton onClick={onClose} type="button" aria-label="Close">
              <Icon name="x" size="medium" />
            </CloseButton>
          </ModalHeaderTopRow>
        </ModalHeader>
        <SplitContainer>
          <DocumentBrowserPanel>
            <DocumentBrowser
              documentsBySource={documentsBySource}
              precedenceOrder={precedenceOrder}
              selectedDocument={selectedDocument}
              initialSource={initialSource}
              onSelectDocument={onSelectDocument}
              translations={{
                searchPlaceholder: t.searchPlaceholder,
                searchAriaLabel: t.searchAriaLabel,
                clearSearchAriaLabel: t.clearSearchAriaLabel,
                noDocuments: t.noDocuments,
                sourceTitles: t.sourceTitles,
                documentTypes: t.documentTypes,
                important: t.important,
              }}
              locale={locale}
            />
          </DocumentBrowserPanel>

          <PDFPanel>
            <PDFViewerPanel
              document={selectedDocument}
              translations={{
                noDocumentSelected: t.noDocumentSelected,
                selectDocumentHint: t.selectDocumentHint,
                important: t.important,
                noPreviewAvailable: t.noPreviewAvailable,
                noPreviewHint: t.noPreviewHint,
                documentTypes: t.documentTypes,
              }}
              locale={locale}
              getPdfProxyUrl={getPdfProxyUrl}
            />
          </PDFPanel>
        </SplitContainer>
      </ModalContainer>
    </Modal>
  )
}
